import React, { lazy, Suspense, useEffect, useMemo, useState } from "react"
import { IEnv, initRequest } from "lib/request"
import { Route, Switch, useHistory, useLocation } from "react-router-dom"
import { general, session, useStore } from "store"
import config from "config"
import { getHost, isMolaSkin, isPortSkin } from "lib/helper"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { appLoaderAtom, isMobileAtom, randomKeyAtom, themeAtom } from "atoms/app.atom"
import RequestInspector from "lib/nanui/RequestInspector"
import Favicon from "react-favicon"
import faviconDevAsset from "assets/images/logos/favicon-dev.png"
import faviconAldeiaAsset from "assets/images/logos/favicon-aldeia.png"
import faviconMolaAsset from "assets/images/logos/favicon-mola.png"
import faviconInspectionAsset from "assets/images/logos/favicon-inspection.png"
import AppLoader from "components-old/ui/AppLoader"
import RedirectLogin from "RedirectLogin"
import ReloadPrompt from "components/feedback/ReloadPrompt"
import { ErrorBoundary } from "react-error-boundary"
import useRedirected from "hooks/useRedirected"
import LegacyUserPath from "layouts/LegacyUserPath"
import PartnerCheckout from "pages/auth/PartnerCheckout.tsx"
import { IS_AGENT_APP, useAldeia } from "hooks/useAldeia"
import { Toaster } from "components/ui/Sonner"
import { cn } from "lib/utils"
import { QueryClientProvider } from "react-query"
import { queryClient } from "main"
import AuthAldeiaFromKinbox from "layouts/AuthAldeiaFromKinbox"

const AuthLayout = lazy(() => import("./layouts/AuthLayout"))
const BasicLayoutAuth = lazy(() => import("./layouts/BasicLayoutAuth"))
const Reset = lazy(() => import("./pages/auth/Reset"))
const Forgot = lazy(() => import("./pages/auth/Forgot"))
const JoinRegister = lazy(() => import("./pages/auth/JoinRegister"))
const JoinWorkspace = lazy(() => import("./pages/auth/JoinWorkspace"))
const InvoicePrint = lazy(() => import("./pages/settings/billing/InvoicePrint"))
const EmailConfirm = lazy(() => import("./pages/auth/EmailConfirm"))
const AdminViewWorkspace = lazy(() => import("./pages/auth/AdminViewWorkspace"))
const NextViewWorkspace = lazy(() => import("./pages/auth/InsiderViewWorkspace"))
const InvoicePrintV2 = lazy(() => import("./pages/settings/billing/InvoicePrintV2"))
const AgentChat = lazy(() => import("./pages/agents/components/AgentChatPreview/AgentChat"))
const RpgChat = lazy(() => import("./pages/agents/components/RpgChat/RpgAgentChat"))

const _isProduction = import.meta.env?.PROD && import.meta.env.VITE_ENV !== "staging"

function AppRouter() {
  const generalStore = useStore(general)
  const sessionStore = useStore(session)
  const setRandomKey = useSetAtom(randomKeyAtom)
  const theme = useAtomValue(themeAtom)
  const appLoader = useAtomValue(appLoaderAtom)
  const [isInspectionMode, setIsInspectionMode] = useState(false)
  const [isMobile, setIsMobile] = useAtom(isMobileAtom)
  const location = useLocation()
  const { isAldeiaLocal } = useAldeia()
  const [changingProduct, setChangingProduct] = useState(false)
  useRedirected()

  useEffect(() => {
    if (getHost().subdomain === "dev") {
      setChangingProduct(true)
      setTimeout(() => {
        setChangingProduct(false)
      }, 200)
    }
  }, [isAldeiaLocal])

  const faviconAsset = useMemo(() => {

    if (IS_AGENT_APP) {
      return faviconAldeiaAsset
    }

    if (isMolaSkin()) {
      return faviconMolaAsset
    }

    if (isInspectionMode) {
      return faviconInspectionAsset
    }

    if (!_isProduction) {
      return faviconDevAsset
    }
  }, [isMolaSkin(), isInspectionMode])

  useEffect(() => {
    try {
      switch (theme) {
        case "light":
          ;(window as any).themeColor.content = "#fff"
          break
        case "blue":
          ;(window as any).themeColor.content = "#0078d4"
          break
        case "stone":
          ;(window as any).themeColor.content = "#efeae7"
          break
        case "purple":
          ;(window as any).themeColor.content = "#444791"
          break
        case "port":
          ;(window as any).themeColor.content = "#405584"
          break

        case "noir":
          ;(window as any).themeColor.content = "#3d3d3d"
          break

        case "dark":
          ;(window as any).themeColor.content = "#2f3136"
          break
      }
    } catch (e) {
      console.log(e)
    }
  }, [theme])

  const history = useHistory()

  useEffect(() => {
    const { subdomain } = getHost()
    if (subdomain == "pay" && location.pathname?.split("/")[1] !== "c") {
      window.location.href = "https://go.kinbox.com.br"
    }
  }, [location.pathname])

  useEffect(() => {
    initRequest({
      history,
      defaultEnv: _isProduction ? config.envs[1] : config.envs[0],
    })
    generalStore?.recoverDomain()
    sessionStore?.recoverSession()

    const { subdomain } = getHost()

    if (subdomain == "view" || subdomain == "partner-inspect" || subdomain == "partner-inspect2") {
      setIsInspectionMode(true)
    }
    if (subdomain == "mobile" || window.innerWidth < 768) {
      document.body.classList.add("mobile")
      setIsMobile(true)
    }

    if (isMolaSkin()) {
      document.body.classList.add("mola")
    }

    if (isPortSkin()) {
      document.body.classList.add("port")
    }

    // Tocar som apenas em uma aba aberta
    var randomKey = Math.random() * 10000 + ""
    setRandomKey(randomKey)
    localStorage.setItem("tabKey", randomKey)

    //when main tab closes, remove item from localStorage
    window.addEventListener("unload", function () {
      if (localStorage.getItem("tabKey") == randomKey) {
        localStorage.removeItem("tabKey")
      }
    })

    if (isMobile) {
      document.ontouchmove = function (e) {
        e.preventDefault()
      }
    }

    //when non-main tabs receive the "close" event,
    //the first one will set the `tabKey` to its `randomKey`
    window.addEventListener("storage", function (evt) {
      if (evt.key == "tabKey" && evt.newValue == null) {
        if (localStorage.getItem("tabKey") == null || localStorage.getItem("tabKey") == undefined) {
          localStorage.setItem("tabKey", randomKey)
        }
      }
    })

    try {
      document.getElementById("initial-loading-wrapper")!.style.opacity = "0"
      document.getElementById("initial-loading-wrapper")!.style.zIndex = "-1"

      setTimeout(() => {
        const element = document.getElementById("initial-loading-wrapper")
        if (element) {
          element.remove()
        }
      }, 1000)
    } catch (e) {}
  }, [])

  if (changingProduct) {
    return <AppLoader />
  }

  return (
    <ErrorBoundary
      onError={(error, info) => {
        console.log(error, info)
        // try {
        //   if (sessionStore?.workspace.id == "1684") {
        //     logtail.error("Erro na página WS" + sessionStore?.workspace?.id, {
        //       error: JSON.stringify(error),
        //       info: JSON.stringify(info),
        //     })
        //   }
        // } catch (e) {
        //   console.log(e)
        // }
      }}
      fallback={<></>}
      key={isAldeiaLocal ? "aldeia" : "kinbox"}
    >
      {/* <Suspense fallback={<AppLoader />}> */}
      <Suspense fallback={<AppLoader />}>
        {appLoader && <AppLoader />}
        <Switch>
          <Route path="/c/:code">
            <PartnerCheckout />
          </Route>
          <Route path="/view-workspace/:token">
            <AdminViewWorkspace />
          </Route>
          <Route path="/next-workspace/:token">
            <NextViewWorkspace />
          </Route>
          <Route path="/auth/email-confirm">
            <EmailConfirm />
          </Route>
          <Route path="/invoices/:id/:transactionId">
            <InvoicePrint />
          </Route>
          <Route path="/invoice/:encoded">
            <InvoicePrintV2 />
          </Route>
          <Route path="/auth/join-workspace">
            <JoinWorkspace />
          </Route>
          <Route path="/auth/join/register">
            <JoinRegister />
          </Route>
          <Route path="/auth/forgot">
            <Forgot />
          </Route>
          <Route path="/auth/reset">
            <Reset />
          </Route>
          <Route path="/auth/kinbox">
            <AuthAldeiaFromKinbox />
          </Route>
          <Route path="/auth">
            <AuthLayout />
          </Route>
          <Route path="/user">
            <LegacyUserPath />
          </Route>
          <Route path="/embed/chat/:id">
            <QueryClientProvider client={queryClient}>
              <AgentChat />
            </QueryClientProvider>
          </Route>
          {sessionStore?.user.email ? (
            <Route path="/">
              <BasicLayoutAuth key={isAldeiaLocal ? "aldeia" : "kinbox"} />
            </Route>
          ) : (
            !sessionStore?.user.isRecovering && <RedirectLogin />
          )}
        </Switch>
        {!_isProduction && <RequestInspector envs={config.envs} left onChange={(env: IEnv) => generalStore?.setDomain(env)} />}
        {faviconAsset && <Favicon url={faviconAsset} />}
        <ReloadPrompt />
      </Suspense>
      <Toaster
        position={isAldeiaLocal ? (theme === "dark" ? "top-center" : "bottom-right") : "top-center"}
        icons={{
          success: (
            <IconMageCheckCircleFill
              className={cn(
                "text-green-600 size-5",
                isAldeiaLocal
                  ? "!text-green-500 outline outline-4 outline-green-500/10 rounded-full size-5"
                  : "text-md dark:!text-green-50",
              )}
            />
          ),
          error: isAldeiaLocal ? (
            <IconMageExclamationCircleFill className="!text-red-500 outline outline-4 outline-red-500/10 rounded-full size-5" />
          ) : (
            <i className="text-lg fas fa-exclamation-circle fc" />
          ),
          info: isAldeiaLocal ? (
            <IconMageInformationCircleFill className="!text-blue-500 outline outline-4 outline-blue-500/10 rounded-full size-5" />
          ) : (
            <i className="text-lg fas fa-info-circle fc" />
          ),
          warning: isAldeiaLocal ? (
            <IconMageExclamationCircleFill className="!text-amber-500 outline outline-4 outline-amber-500/10 rounded-full size-5" />
          ) : (
            <i className="text-lg fas fa-exclamation-circle fc" />
          ),
          loading: <i className="fas fa-spinner-third toast-loading text-md aldeia:text-blue-400" />,
        }}
      />
    </ErrorBoundary>
  )
}

export default AppRouter
